/*
 * App installation status — platform-wide view of DHIS2 app clients (the
 * embedded TallyStream app's credential records on each paired instance)
 * with their last-used heartbeat and rotation status. Built on the
 * existing /api/saas/app-clients listing; no new backend.
 *
 * For active/revoked breakdown + per-instance fan-out, this surface is
 * the read-only counterpart of AdminAppClients (which handles authoring).
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import TableToolbar from "../../components/TableToolbar.js";
import { useTableState } from "../../lib/useTableState.js";
import { api, ApiError, DhisAppClient } from "../../lib/api.js";

function staleness(lastUsedAt?: string): { label: string; tone: "ok" | "warn" | "stale" } {
  if (!lastUsedAt) return { label: "never", tone: "stale" };
  const ageMs = Date.now() - Date.parse(lastUsedAt);
  if (Number.isNaN(ageMs)) return { label: lastUsedAt, tone: "stale" };
  const days = Math.floor(ageMs / 86400000);
  if (days < 1) return { label: "today", tone: "ok" };
  if (days < 7) return { label: `${days}d ago`, tone: "ok" };
  if (days < 30) return { label: `${days}d ago`, tone: "warn" };
  return { label: `${days}d ago`, tone: "stale" };
}

export default function AdminAppInstallations() {
  const [clients, setClients] = useState<DhisAppClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setError(null);
    api.listAppClients()
      .then((r) => setClients(r.clients))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }
  useEffect(refresh, []);

  const stats = useMemo(() => {
    const list = clients ?? [];
    const active = list.filter((c) => c.status === "active").length;
    const revoked = list.filter((c) => c.status === "revoked").length;
    const stale = list.filter((c) => staleness(c.lastUsedAt).tone === "stale").length;
    const byInstance = new Map<string, number>();
    for (const c of list) {
      const key = c.dhis2BaseUrl ?? "(no instance)";
      byInstance.set(key, (byInstance.get(key) ?? 0) + 1);
    }
    return { total: list.length, active, revoked, stale, byInstance };
  }, [clients]);

  const t = useTableState((clients ?? []) as unknown as Array<Record<string, unknown>>, {
    initialPageSize: 25,
    searchFields: ["clientId", "label", "workspaceId", "dhis2BaseUrl", "status"],
    initialSortKey: "lastUsedAt",
    initialSortDir: "desc"
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>App installation status</h1>
          <p className="muted">
            Per-workspace TallyStream app installations on paired DHIS2 instances. Each row is the
            credential record the embedded app uses to call back to the platform; the heartbeat
            column reflects when that client was last seen on this platform.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <Stat label="Total installations" value={stats.total} />
          <Stat label="Active" value={stats.active} accent="ok" />
          <Stat label="Revoked" value={stats.revoked} accent={stats.revoked > 0 ? "warn" : "muted"} />
          <Stat label="Stale (≥30d)" value={stats.stale} accent={stats.stale > 0 ? "warn" : "muted"} />
          <Stat label="Distinct DHIS2 instances" value={stats.byInstance.size} />
        </div>
      </section>

      {clients === null ? (
        <p className="muted">Loading…</p>
      ) : clients.length === 0 ? (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No app clients registered</h2>
          <p>
            Each workspace's embedded DHIS2 app needs a registered client. Open
            <strong> Administration → DHIS2 app clients</strong> to author one, or run the
            "Discover app" action on a paired DHIS2 instance.
          </p>
        </div>
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="client / workspace / instance / status…"
            exportFilename="app-installations"
            exportColumns={["clientId", "label", "workspaceId", "dhis2BaseUrl", "status", "createdAt", "lastUsedAt", "secretLastRotatedAt"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Client</th><th>Label</th><th>Workspace</th>
                <th>DHIS2 instance</th><th>Status</th><th>Last used</th><th>Secret rotated</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as DhisAppClient[]).map((c) => {
                const heartbeat = staleness(c.lastUsedAt);
                const heartbeatColor = heartbeat.tone === "ok" ? "#065f46"
                  : heartbeat.tone === "warn" ? "#92400e" : "#7f1d1d";
                return (
                  <tr key={c.clientId}>
                    <td className="mono" style={{ fontSize: 11 }}>{c.clientId}</td>
                    <td>{c.label}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{c.workspaceId}</td>
                    <td className="mono" style={{ fontSize: 11, wordBreak: "break-all" }}>{c.dhis2BaseUrl ?? "—"}</td>
                    <td>
                      <span className={`pill pill-${c.status === "active" ? "ok" : "err"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td style={{ color: heartbeatColor, fontSize: 11 }}>{heartbeat.label}</td>
                    <td style={{ fontSize: 11 }}>{c.secretLastRotatedAt ? new Date(c.secretLastRotatedAt).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "ok" | "warn" | "muted" }) {
  const color = accent === "ok" ? "#065f46" : accent === "warn" ? "#92400e" : accent === "muted" ? "#475569" : "#1e293b";
  const bg = accent === "ok" ? "#d1fae5" : accent === "warn" ? "#fef3c7" : "#f1f5f9";
  return (
    <div style={{ background: bg, color, padding: "10px 12px", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value.toLocaleString()}</div>
    </div>
  );
}
